
import { isEqual, sortBy } from 'lodash';

import { ViQuestionApi } from '@api/visibilityIntelligence/models/vi-question.api.model';
import { ViQuestionSetApi } from '@api/visibilityIntelligence/models/vi-question-set.api.model';
import { ViMonitorApi } from '@api/visibilityIntelligence/models/vi-monitor.api.model';
import { ViCompetitorApi } from '@api/visibilityIntelligence/models/vi-competitor.api.model';
import { ViBrandApi } from '@api/visibilityIntelligence/models/vi-brand.api.model';

import { ViCadenceEnum } from '../enums/vi-cadence.enum';
import { ViMonitorStatusEnum } from '../enums/vi-monitor-status.enum';

import { AnswerEngineTypesEnum } from '../enums/answer-engine-types.enum';
import { BaseModel } from '../../Common/Basic/models/base.model';
import { AuditableModel } from '../../Common/Misc/models/auditable.model';
import { viApiToEngineType, engineTypeToViApi } from '../helpers/vi-engine-type.map';
import { ViMonitorQuestionContext } from './vi-monitor-question-context.model';
import { ViBrandIdentity } from './vi-brand-identity.model';

const EDITABLE_STATUSES: ViMonitorStatusEnum[] = [
    ViMonitorStatusEnum.draft,
    ViMonitorStatusEnum.paused
];

const NO_RESULTS_STATUSES: ViMonitorStatusEnum[] = [
    ViMonitorStatusEnum.draft
];


export class ViMonitor extends BaseModel<ViMonitor, ViMonitorApi> implements AuditableModel {

    id: number;
    name: string;

    agentId: number;
    questions?: ViQuestionApi[];
    brand?: ViBrandIdentity | null;
    competitors?: ViBrandIdentity[];
    language?: string;
    isCompetitiveBenchmarking?: boolean;

    dimensionWeights?: Record<string, number> | null;
    answerEngines?: AnswerEngineTypesEnum[];
    cadence?: ViCadenceEnum | null;
    scheduleTimezone?: string;

    analysisFocus?: string[];

    questionSets?: ViQuestionSetApi[];
    activeQuestionSet?: ViQuestionSetApi | null;

    isHidden?: boolean;
    isFavourite?: boolean;
    displayGroupId?: number;

    readonly status?: ViMonitorStatusEnum;

    readonly nextRunAt?: Date | null;
    readonly lastRunAt?: Date | null;

    readonly createdDate?: Date;
    readonly createdByUserId?: number;

    readonly modifiedDate?: Date;
    readonly modifiedByUserId?: number;

    constructor(apiModel?: ViMonitorApi) {
        super();
        if (apiModel) {
            this.status = apiModel.status;
            this.nextRunAt = apiModel.nextRunAt ? new Date(apiModel.nextRunAt) : null;
            this.lastRunAt = apiModel.lastRunAt ? new Date(apiModel.lastRunAt) : null;

            this.createdDate = apiModel.createdAt ? new Date(apiModel.createdAt) : null;
            this.createdByUserId = apiModel.createdByUserId;

            this.modifiedDate = apiModel.updatedAt ? new Date(apiModel.updatedAt) : null;
            this.modifiedByUserId = apiModel.lastUpdatedByUserId;

            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    get isEditable(): boolean {
        return EDITABLE_STATUSES.includes(this.status);
    }

    get hasNoResults(): boolean {
        return NO_RESULTS_STATUSES.includes(this.status);
    }

    /**
     * Answer engines, analysis focus, and language are all required before a monitor can start generating questions
     */
    get isMonitorSettingsComplete(): boolean {
        return !!this.answerEngines?.length && !!this.analysisFocus?.length && !!this.language;
    }

    /**
     * Brand name, websites, industry, and bio are all required before a monitor can start generating questions
     */
    get isBrandComplete(): boolean {
        return !!this.brand?.name?.trim()
            && !!this.brand?.websites?.length
            && !!this.brand?.industry?.trim()
            && !!this.brand?.bio?.trim();
    }

    /**
     * Question and at least one answer signal are required
     */
    get isQuestionsComplete(): boolean {
        return !!this.questions?.length &&
        !this.questions.some(question => !question.question?.trim() || !question.expectedAnswerSignals?.length);
    }

    constructFromApiModel(apiModel: ViMonitorApi): void {
        this.id = +apiModel.id;
        this.name = apiModel.name ?? '';

        this.agentId = +apiModel.agentId;
        this.questions = apiModel.questions;

        this.brand = apiModel.brand ? {
            brandId: apiModel.brand.brandIdentityId ? +apiModel.brand.brandIdentityId : undefined,

            name: apiModel.brand.primaryName ?? '',
            nameVariations: apiModel.brand.nameVariations ?? [],
            websites: apiModel.ownedDomains ?? [],
            industry: apiModel.brand.industry ?? '',
            bio: apiModel.brand.companyOverview ?? '',
            additionalContext: apiModel.brand.additionalContext ?? [],
        } as ViBrandIdentity : null;

        if (apiModel.brand) {
            this.brand = new ViBrandIdentity(apiModel.brand);
            this.brand.websites = apiModel.ownedDomains ?? [];
        } else {
            this.brand = null;
        }

        this.competitors = apiModel.competitors?.map(competitor => ({
            brandId: competitor.competitorBrandIdentityId ? +competitor.competitorBrandIdentityId : undefined,
            id: competitor.id ? +competitor.id : undefined,
            name: competitor.competitorName ?? '',
            nameVariations: competitor.competitorNameVariations ?? [],
            websites: competitor.competitorWebsites ?? [],
        } as ViBrandIdentity)) ?? [];
        this.language = apiModel.language ?? undefined;
        this.isCompetitiveBenchmarking = !!apiModel.isCompetitiveBenchmarking;

        this.dimensionWeights = apiModel.dimensionWeights;

        this.answerEngines = apiModel.answerEngines?.map(type => viApiToEngineType[type]) ?? [];
        this.cadence = apiModel.cadence;
        this.scheduleTimezone = apiModel.scheduleTimezone ?? 'UTC';

        this.analysisFocus = apiModel.targetKeywords ?? [];

        this.questionSets = apiModel.questionSets;
        this.activeQuestionSet = apiModel.activeQuestionSet;

        this.isHidden = !!apiModel.isHidden;
        this.isFavourite = !!apiModel.isFavourite;
        this.displayGroupId = apiModel.displayGroupId ?? undefined;
    }

    constructDefault(): void {
        this.scheduleTimezone = 'UTC';
    }

    toApiModel(): ViMonitorApi {
        return {
            id: this.id?.toString(),
            name: this.name,
            agentId: this.agentId?.toString(),
            questions: this.questions,
            brand: this.brand ? {
                brandIdentityId: this.brand.brandId ? this.brand.brandId + '' : undefined,
                primaryName: this.brand.name ?? '',
                nameVariations: this.brand.nameVariations ?? [],
                industry: this.brand.industry ?? '',
                companyOverview: this.brand.bio ?? '',
                additionalContext: this.brand.additionalContext ?? []
            } as ViBrandApi : null,
            competitors: this.competitors?.map(competitor => ({
                id: competitor.id?.toString(),
                competitorBrandIdentityId: competitor.brandId ? competitor.brandId + '' : undefined,
                competitorName: competitor.name,
                competitorNameVariations: competitor.nameVariations ?? [],
                competitorWebsites: competitor.websites ?? []
            } as ViCompetitorApi)) ?? [],

            ownedDomains: this.brand?.websites ?? [],
            language: this.language,
            isCompetitiveBenchmarking: this.isCompetitiveBenchmarking,
            dimensionWeights: this.dimensionWeights,
            answerEngines: this.answerEngines?.map(type => engineTypeToViApi[type]),
            cadence: this.cadence,
            scheduleTimezone: this.scheduleTimezone,
            targetKeywords: this.analysisFocus ?? [],
            questionSets: this.questionSets,
            activeQuestionSet: this.activeQuestionSet,
            isHidden: this.isHidden,
            isFavourite: this.isFavourite,
            displayGroupId: this.displayGroupId,
        };
    }

    clone(): ViMonitor {
        const clone = new ViMonitor({
            ...this.toApiModel(),
            status: this.status,
            nextRunAt: this.nextRunAt?.toISOString(),
            lastRunAt: this.lastRunAt?.toISOString(),
            createdAt: this.createdDate?.toISOString(),
            createdByUserId: this.createdByUserId,
            updatedAt: this.modifiedDate?.toISOString(),
            lastUpdatedByUserId: this.modifiedByUserId
        });

        return clone;
    }

    equals(other?: ViMonitor): boolean {
        if (!other) {
            return false;
        }

        if (!this.idsEqual(this.id, other.id)) {
            return false;
        }

        if ((this.name ?? '') !== (other.name ?? '')) {
            return false;
        }

        if (this.status !== other.status) {
            return false;
        }

        if (!this.idsEqual(this.agentId, other.agentId)) {
            return false;
        }

        if (this.language !== other.language) {
            return false;
        }

        if (!!this.isCompetitiveBenchmarking !== !!other.isCompetitiveBenchmarking) {
            return false;
        }

        if ((this.cadence ?? null) !== (other.cadence ?? null)) {
            return false;
        }

        if ((this.scheduleTimezone ?? null) !== (other.scheduleTimezone ?? null)) {
            return false;
        }

        if (!!this.isHidden !== !!other.isHidden) {
            return false;
        }

        if (!!this.isFavourite !== !!other.isFavourite) {
            return false;
        }

        if (!this.idsEqual(this.displayGroupId, other.displayGroupId)) {
            return false;
        }

        if (!isEqual(this.normalizeBrandIdentity(this.brand), this.normalizeBrandIdentity(other.brand))) {
            return false;
        }

        if (!isEqual(this.normalizeCompetitors(this.competitors), this.normalizeCompetitors(other.competitors))) {
            return false;
        }

        if (!isEqual(sortBy(this.questions ?? [], 'question'), sortBy(other.questions ?? [], 'question'))) {
            return false;
        }

        if (!isEqual(this.dimensionWeights ?? null, other.dimensionWeights ?? null)) {
            return false;
        }

        if (!isEqual([...(this.answerEngines ?? [])].sort(), [...(other.answerEngines ?? [])].sort())) {
            return false;
        }

        if (!isEqual([...(this.analysisFocus ?? [])].sort(), [...(other.analysisFocus ?? [])].sort())) {
            return false;
        }

        if (!isEqual(sortBy(this.questionSets ?? [], 'id'), sortBy(other.questionSets ?? [], 'id'))) {
            return false;
        }

        if (!isEqual(this.activeQuestionSet ?? null, other.activeQuestionSet ?? null)) {
            return false;
        }

        if (!this.datesEqual(this.nextRunAt, other.nextRunAt)) {
            return false;
        }

        if (!this.datesEqual(this.createdDate, other.createdDate)) {
            return false;
        }

        if (!this.datesEqual(this.modifiedDate, other.modifiedDate)) {
            return false;
        }

        return true;
    }


    /** The subset of this monitor's setup that would make previously-generated questions stale if changed */
    getQuestionRelevantContext(): ViMonitorQuestionContext {
        return {
            analysisFocus: [...(this.analysisFocus ?? [])].sort(),
            isCompetitiveBenchmarking: !!this.isCompetitiveBenchmarking,
            brand: this.normalizeBrandIdentity(this.brand),
            competitors: this.normalizeCompetitors(this.competitors)
        };
    }

    /** Order-independent comparison helper: normalizes a brand/competitor's reorderable array fields before comparing */
    private normalizeBrandIdentity(brand?: ViBrandIdentity | null): ViBrandIdentity | null {
        if (!brand) {
            return null;
        }
        return {
            ...brand,
            // id is optional, so a missing key and an explicit undefined must compare as equal
            id: brand.id ?? undefined,
            nameVariations: [...(brand.nameVariations ?? [])].sort(),
            websites: [...(brand.websites ?? [])].sort(),
            additionalContext: sortBy(brand.additionalContext ?? [], 'contextTitle')
        } as ViBrandIdentity;
    }

    private normalizeCompetitors(competitors?: ViBrandIdentity[]): ViBrandIdentity[] {
        return sortBy((competitors ?? []).map(competitor => this.normalizeBrandIdentity(competitor)), 'name') as ViBrandIdentity[];
    }

    /** Date-valued fields are reference types, so a plain !== would always be true for two distinct-but-equal Date instances */
    private datesEqual(a?: Date | null, b?: Date | null): boolean {
        return (a ?? null)?.getTime() === (b ?? null)?.getTime();
    }

    /** id can be NaN, undefined, and null */
    private idsEqual(a?: number | null, b?: number | null): boolean {
        return (Number.isNaN(a) ? null : (a ?? null)) === (Number.isNaN(b) ? null : (b ?? null));
    }
}
